/**
 * Locale-aware date formatting for the review UI.
 *
 * Receipts print dates in their local format — Spanish and French use
 * DD/MM/YYYY, US English uses MM/DD/YYYY. Showing the date back to the
 * user in the OPPOSITE format is confusing and easy to misread, so we
 * format based on the language the user selected for OCR (which matches
 * the receipt's locale).
 */

/**
 * `YYYY-MM-DD` from the LOCAL calendar date, for exports. The parser
 * creates receipt dates at local midnight (date-fns `parse`, and
 * `parseDateForLang` below), so `toISOString()` — which reads UTC —
 * would shift the day back for every user east of Greenwich and make
 * the export disagree with the on-screen date. Local getters keep the
 * exported date identical to what the user sees and confirmed.
 */
export function localIsoDate(d: Date): string {
  const yyyy = String(d.getFullYear()).padStart(4, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export function formatDateForLang(d: Date, lang: string): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  if (lang === 'eng') return `${mm}/${dd}/${yyyy}`;
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Parse a user-typed date string back to a Date, interpreting the order
 * (day-first vs month-first) according to `lang`. Accepts `/`, `-`, `.`
 * as separators. Two-digit years are assumed to be 21st century.
 */
export function parseDateForLang(s: string, lang: string): Date | null {
  const trimmed = s.trim();
  const m = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/.exec(trimmed);
  if (m === null) return null;
  const a = parseInt(m[1] ?? '', 10);
  const b = parseInt(m[2] ?? '', 10);
  let y = parseInt(m[3] ?? '', 10);
  if (Number.isNaN(a) || Number.isNaN(b) || Number.isNaN(y)) return null;
  if ((m[3] ?? '').length === 2) y += 2000;

  const day = lang === 'eng' ? b : a;
  const month = lang === 'eng' ? a : b;
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  const d = new Date(y, month - 1, day);
  if (
    d.getFullYear() !== y ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day
  ) {
    return null;
  }
  return d;
}
